import { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { StudentDashboardStateKey } from 'AdmissionsGuidance/StudentDashboard.types';
import { type Brand } from 'AppBranding';
import ClientStorage from 'ClientStorage';
import useIsMobileMixin from 'StudentNetwork/react/useIsMobileMixin';
import { useCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { ProgramType } from 'Program';
import { Icon } from 'FrontRoyalIcon';
import { Text } from 'Text';
import './locales/welcomeMessage/welcomeMessage-en.json';
import useStudentDashboardConfig from 'AdmissionsGuidance/useStudentDashboardConfig';
import { twMerge } from 'tailwind-merge';
import { InlineLink } from 'InlineLink';
import { getCohort } from 'Users';
import { getApplicationDeadline } from 'Cohorts';
import { useWelcomeMessageConfig } from './hooks/useWelcomeMessageConfig';
import { fitTextToContainer } from './utils';

type WelcomeMessageProps = {
    studentDashboardStateKey: StudentDashboardStateKey;
    appMenuProgramLogo: string | null;
    branding: Brand;
    brandNameLong: string;
    messageIsDismissed: boolean;
    setMessageIsDismissed: Dispatch<SetStateAction<boolean>>;
    isMobile: boolean;
    programType: ProgramType;
    userName?: string;
    applicationDeadlineDate: Date | null;
};

export const WelcomeMessagePresentation: FC<WelcomeMessageProps> = ({
    studentDashboardStateKey,
    appMenuProgramLogo,
    branding,
    brandNameLong,
    messageIsDismissed,
    setMessageIsDismissed,
    isMobile,
    programType,
    userName,
    applicationDeadlineDate,
}) => {
    const welcomeStudentRef = useRef<HTMLDivElement>(null);
    const { firstMessageText, secondMessageText, textBubbleShown } = useWelcomeMessageConfig({
        messageIsDismissed,
        isMobile,
        studentDashboardStateKey,
        programType,
        brandNameLong,
        applicationDeadlineDate,
        userName,
    });

    useEffect(() => {
        const el = welcomeStudentRef.current;
        const handleResize = () => fitTextToContainer(el);
        const observer = new ResizeObserver(handleResize);
        if (el?.parentElement) observer.observe(el.parentElement);
        // initial fit
        handleResize();

        return () => observer.disconnect();
    }, [welcomeStudentRef, firstMessageText, textBubbleShown]);

    const onDismiss = useCallback(() => {
        ClientStorage.setItem('mobile_top_message_explore_the_curriculum_hidden', 'true');
        setMessageIsDismissed(true);
    }, [setMessageIsDismissed]);

    if (!firstMessageText) return null;

    return (
        <div className="flex flex-row items-end">
            {appMenuProgramLogo && (
                <div className="mr-3.5 shrink-0">
                    <img className="h-[45px]" src={appMenuProgramLogo} alt={`${branding} logo`} />
                </div>
            )}

            <div
                className={twMerge(
                    'relative rounded-r-6 rounded-t-6 border-slate-grey-200 bg-white',
                    textBubbleShown ? 'w-full grow border p-[15px] pr-[45px] opacity-100' : 'h-0 w-0 opacity-0',
                    'transition-opacity duration-300 ease-in-out',
                )}
            >
                {isMobile ? (
                    <>
                        <Text color="black" className="mb-0">
                            {firstMessageText}
                        </Text>
                        <InlineLink weight="semibold" href="/settings/application">
                            {secondMessageText} <i className="fa fa-arrow-right" />
                        </InlineLink>
                    </>
                ) : (
                    <>
                        <Text color="black" className="mb-5">
                            {firstMessageText}
                        </Text>
                        <Text color="black">{secondMessageText}</Text>
                    </>
                )}

                {/* Dismiss button */}
                <button
                    type="button"
                    className="absolute right-0 top-0 p-[15px] text-slate-grey hover:text-black"
                    onClick={onDismiss}
                >
                    <Icon iconName="Close16" />
                </button>

                {/* Text bubble arrow */}
                <div className="absolute bottom-[-1px] left-[-11px] h-0 w-0 border-b-[11px] border-l-[11px] border-b-slate-grey-200 border-l-transparent">
                    <div className="absolute bottom-[-10px] left-[-9px] h-0 w-0 border-b-[9px] border-l-[9px] border-b-white border-l-transparent" />
                </div>
            </div>

            <div
                className={twMerge(
                    'flex h-[45px] min-w-0 items-center font-semibold text-black',
                    textBubbleShown ? 'w-0 opacity-0' : 'w-full opacity-100',
                    'transition-opacity duration-300 ease-in-out',
                )}
            >
                <div ref={welcomeStudentRef} className="text-nowrap leading-[45px]">
                    {firstMessageText}
                </div>
            </div>
        </div>
    );
};

export const WelcomeMessage: FC = () => {
    const config = useStudentDashboardConfig();
    const { appMenuProgramLogo, branding, brandNameLong } = useTargetBrandConfig();
    const { isMobile } = useIsMobileMixin();
    const [messageIsDismissed, setMessageIsDismissed] = useState(
        ClientStorage.getItem('mobile_top_message_explore_the_curriculum_hidden') === 'true',
    );
    const currentUser = useCurrentUser();
    const cohort = getCohort(currentUser);

    if (!cohort || !currentUser) return null;

    const applicationDeadline = getApplicationDeadline(cohort);
    const userFirstName = currentUser.nickname || currentUser.name.split(' ')[0];

    return (
        <WelcomeMessagePresentation
            studentDashboardStateKey={config?.stateKey ?? StudentDashboardStateKey.signup}
            appMenuProgramLogo={appMenuProgramLogo}
            branding={branding}
            brandNameLong={brandNameLong}
            messageIsDismissed={messageIsDismissed}
            setMessageIsDismissed={setMessageIsDismissed}
            isMobile={isMobile}
            programType={cohort.programType ?? ProgramType.mba}
            userName={userFirstName}
            applicationDeadlineDate={applicationDeadline}
        />
    );
};
